import React, { useMemo, useState } from 'react';
import {
  FiActivity,
  FiAlertCircle,
  FiAward,
  FiChevronRight,
  FiHome,
} from 'react-icons/fi';
import { EmptyState } from './states';
import { PMS_LABELS, fmtMoney, fmtNumber } from './derive';

const TABS = [
  { id: 'activity', label: 'Activity', icon: FiActivity },
  { id: 'leaders', label: 'Leaders', icon: FiAward },
  { id: 'anomalies', label: 'Anomalies', icon: FiAlertCircle },
];

const formatRelative = (date) => {
  if (!date) return '—';
  const ms = Date.now() - new Date(date).getTime();
  if (!Number.isFinite(ms)) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default function InsightsPanel({
  summary,
  loading = false,
  error,
  onSelectHost,
  onSelectProperty,
  onSelectPms,
}) {
  const [tab, setTab] = useState('activity');
  const activityHosts = summary?.activity?.hosts || [];
  const activityProperties = summary?.activity?.properties || [];
  const topHosts = summary?.leaders?.topHosts || [];
  const topPms = summary?.leaders?.topPms || [];
  const topMarkets = summary?.leaders?.topMarkets || [];

  const anomalies = useMemo(() => {
    const items = [];
    if (error) {
      items.push({
        id: 'summary-error',
        severity: 'error',
        label: 'Dashboard API error',
        detail: String(error),
      });
    }
    if ((summary?.anomalies?.unknownPmsHosts || 0) > 0) {
      items.push({
        id: 'unknown-pms',
        severity: 'warning',
        label: `${fmtNumber(summary.anomalies.unknownPmsHosts)} hosts with unknown PMS`,
        detail: 'Legacy PMS fallback is still missing a direct signal on some host records.',
      });
    }
    if ((summary?.anomalies?.requestedOrders || 0) > 0) {
      items.push({
        id: 'requested-orders',
        severity: 'info',
        label: `${fmtNumber(summary.anomalies.requestedOrders)} requested orders`,
        detail: 'Open Orders records to review current dispatch backlog.',
      });
    }
    return items;
  }, [error, summary]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
      <div className="flex items-center border-b border-zinc-100 px-2">
        {TABS.map((tabDef) => {
          const Icon = tabDef.icon;
          const active = tab === tabDef.id;
          const badge =
            tabDef.id === 'activity'
              ? activityHosts.length + activityProperties.length
              : tabDef.id === 'leaders'
                ? topHosts.length + topPms.length + topMarkets.length
                : anomalies.length;
          return (
            <button
              key={tabDef.id}
              type="button"
              onClick={() => setTab(tabDef.id)}
              aria-pressed={active}
              className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-2 py-2 text-[11.5px] font-medium transition-colors duration-150 motion-reduce:transition-none ${
                active
                  ? 'border-zinc-900 text-zinc-900'
                  : 'border-transparent text-zinc-500 hover:text-zinc-800'
              }`}
            >
              <Icon className="h-3 w-3" />
              {tabDef.label}
              {badge > 0 ? (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                    tabDef.id === 'anomalies'
                      ? 'bg-amber-50 text-amber-700'
                      : 'bg-zinc-100 text-zinc-600'
                  }`}
                >
                  {badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div
        key={tab}
        className="flex-1 overflow-y-auto animate-[fadeInPanel_150ms_ease-out_both] motion-reduce:animate-none"
      >
        {tab === 'activity' ? (
          <ActivityList
            loading={loading}
            hosts={activityHosts}
            properties={activityProperties}
            onSelectHost={onSelectHost}
            onSelectProperty={onSelectProperty}
          />
        ) : tab === 'leaders' ? (
          <LeadersList
            topHosts={topHosts}
            topPms={topPms}
            topMarkets={topMarkets}
            onSelectHost={onSelectHost}
            onSelectPms={onSelectPms}
          />
        ) : (
          <AnomalyList items={anomalies} />
        )}
      </div>

      <style>{`
        @keyframes fadeInPanel {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function ActivityList({ loading, hosts, properties, onSelectHost, onSelectProperty }) {
  if (loading && hosts.length === 0 && properties.length === 0) {
    return (
      <div className="space-y-2 p-3">
        {[0, 1, 2, 3, 4].map((index) => (
          <div key={index} className="h-11 animate-pulse rounded-lg bg-zinc-100" />
        ))}
      </div>
    );
  }

  if (hosts.length === 0 && properties.length === 0) {
    return (
      <EmptyState
        title="No recent activity"
        description="Try widening the date range."
        icon={FiActivity}
      />
    );
  }

  return (
    <div className="divide-y divide-zinc-100">
      {hosts.length > 0 ? (
        <Section title="Recent hosts" count={hosts.length}>
          <ul>
            {hosts.map((host) => (
              <li key={`host-${host._id}`}>
                <button
                  type="button"
                  onClick={() => onSelectHost?.(host)}
                  className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-[10px] font-semibold text-emerald-700">
                    {(host.name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="truncate text-[12px] font-semibold text-zinc-900">{host.name}</p>
                      {host.pmsType && host.pmsType !== 'unknown' ? (
                        <span className="rounded-md bg-indigo-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-indigo-700">
                          {PMS_LABELS[host.pmsType] || host.pmsType}
                        </span>
                      ) : null}
                    </div>
                    <p className="truncate text-[10.5px] text-zinc-500">
                      {host.email || host.phoneNumber || '—'} · {host.propertyCount || 0} props
                    </p>
                  </div>
                  <span className="text-[10px] tabular-nums text-zinc-400 group-hover:text-zinc-600">
                    {formatRelative(host.createdAt)}
                  </span>
                  <FiChevronRight className="h-3 w-3 text-zinc-300 group-hover:text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {properties.length > 0 ? (
        <Section title="Recent properties" count={properties.length}>
          <ul>
            {properties.map((property) => (
              <li key={`property-${property._id}`}>
                <button
                  type="button"
                  onClick={() => onSelectProperty?.(property)}
                  className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
                    <FiHome className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">
                      {property?.address?.line1 || property?.listingTitle || 'Property'}
                    </p>
                    <p className="truncate text-[10.5px] text-zinc-500">
                      {property?.address?.city || ''}{property?.address?.state ? `, ${property.address.state}` : ''}
                      {property.propertySource ? ` · ${PMS_LABELS[property.propertySource] || property.propertySource}` : ''}
                    </p>
                  </div>
                  <span className="text-[10px] tabular-nums text-zinc-400 group-hover:text-zinc-600">
                    {formatRelative(property.createdAt)}
                  </span>
                  <FiChevronRight className="h-3 w-3 text-zinc-300 group-hover:text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}

function LeadersList({ topHosts, topPms, topMarkets, onSelectHost, onSelectPms }) {
  if (topHosts.length === 0 && topPms.length === 0 && topMarkets.length === 0) {
    return (
      <EmptyState
        title="No leaders yet"
        description="Once orders land, top hosts will surface here."
        icon={FiAward}
      />
    );
  }

  return (
    <div className="divide-y divide-zinc-100">
      <Section title="Top hosts by orders" count={topHosts.length}>
        {topHosts.length === 0 ? (
          <p className="px-3 py-3 text-[11px] text-zinc-500">No host orders in scope.</p>
        ) : (
          <ul>
            {topHosts.map((host, index) => (
              <li key={host._id}>
                <button
                  type="button"
                  onClick={() => onSelectHost?.(host)}
                  className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-[10px] font-semibold text-zinc-600">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">{host.name}</p>
                    <p className="truncate text-[10.5px] text-zinc-500">
                      {fmtNumber(host.orderCount || 0)} orders · {fmtMoney(host.revenue || 0)}
                    </p>
                  </div>
                  <FiChevronRight className="h-3 w-3 text-zinc-300 group-hover:text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Top PMS" count={topPms.length}>
        {topPms.length === 0 ? (
          <p className="px-3 py-3 text-[11px] text-zinc-500">No PMS signal yet.</p>
        ) : (
          <ul>
            {topPms.map((row) => (
              <li key={row._id}>
                <button
                  type="button"
                  onClick={() => onSelectPms?.(row._id)}
                  className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">
                      {PMS_LABELS[row._id] || row._id}
                    </p>
                    <p className="truncate text-[10.5px] text-zinc-500">
                      {fmtNumber(row.count)} hosts
                    </p>
                  </div>
                  <FiChevronRight className="h-3 w-3 text-zinc-300 group-hover:text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Top markets" count={topMarkets.length}>
        {topMarkets.length === 0 ? (
          <p className="px-3 py-3 text-[11px] text-zinc-500">No market mix yet.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {topMarkets.map((market) => (
              <li key={market.city} className="flex items-center justify-between px-3 py-2 text-[11px]">
                <span className="font-semibold text-zinc-900">{market.city}</span>
                <span className="tabular-nums text-zinc-500">{fmtNumber(market.count)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function AnomalyList({ items }) {
  if (items.length === 0) {
    return (
      <EmptyState
        title="No anomalies"
        description="This filter set looks clean."
        icon={FiAlertCircle}
      />
    );
  }

  return (
    <div className="space-y-2 p-3">
      {items.map((item) => (
        <div
          key={item.id}
          className={`rounded-lg border p-3 ${
            item.severity === 'error'
              ? 'border-red-200 bg-red-50/70'
              : item.severity === 'warning'
                ? 'border-amber-200 bg-amber-50/70'
                : 'border-zinc-200 bg-zinc-50/80'
          }`}
        >
          <p className="text-[11px] font-semibold text-zinc-900">{item.label}</p>
          <p className="mt-1 text-[10.5px] text-zinc-600">{item.detail}</p>
        </div>
      ))}
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <div>
      <div className="flex items-center justify-between px-3 py-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{title}</p>
        {count ? (
          <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[9px] font-semibold text-zinc-600">
            {fmtNumber(count)}
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}
